/**
 * EditorTimeline — Bottom horizontal timeline with step cards.
 * Supports selection, drag-to-reorder, horizontal scroll, and right-click delete.
 */
import Phaser from 'phaser';
import { CANVAS_W, CANVAS_H, TIMELINE_H, EditorEvents } from './EditorTypes';
import type { EditorState } from './EditorTypes';
import { TECH, fillTechRect, drawTechRect, showConfirmDialog, showSelectionPopup } from '@/ui/TechUI';
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from '../CinematicEvents';
import { getStepMeta, STEP_TYPE_METAS, createDefaultStep, applySmartDialogueDefaults } from './EditorStepFactory';
import { registerSequence } from '../CinematicDirector';
import type { CinematicStep } from '../CinematicTypes';

const CARD_W = 80;
const CARD_H = 90;
const CARD_GAP = 6;
const CARD_PAD = 10;
const TL_Y = CANVAS_H - TIMELINE_H;
const DRAG_THRESHOLD = 15; // px before drag starts (high enough to avoid accidental drags)

/** Extract display duration (ms) from a step, or null if indeterminate */
function getStepDuration(step: any): number | null {
  switch (step.type) {
    case 'dialogue':  return step.autoDismissMs ?? null;
    case 'cameraPan': return step.duration ?? 800;
    case 'fakeClick': return step.holdMs ?? 500;
    case 'wait':      return step.timeoutMs ?? null;
    case 'action':    return 0;
    case 'recap':     return null; // user-controlled
    case 'spotlight': return step.timeoutMs ?? null; // click or event
    default:          return null;
  }
}

/** Format ms to short string (e.g. "2.0s", "800ms", "instant") */
function fmtMs(ms: number | null): string {
  if (ms === null) return '...';
  if (ms === 0)    return 'instant';
  if (ms >= 1000)  return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

export class EditorTimeline {
  private scene: Phaser.Scene;
  private state: EditorState;
  private container: Phaser.GameObjects.Container;
  private scrollContainer: Phaser.GameObjects.Container;
  private scrollX = 0;
  private cards: Phaser.GameObjects.Container[] = [];

  // Drag-to-reorder state
  private dragIndex = -1;
  private dragStartX = 0;
  private isDragging = false;
  private insertionMarker: Phaser.GameObjects.Graphics;

  // Progress bar state
  private playStartTime = 0;
  private progressTimer: Phaser.Time.TimerEvent | null = null;
  /** Graphics objects for progress bars (one per card, created on demand) */
  private progressBars: (Phaser.GameObjects.Graphics | null)[] = [];

  constructor(scene: Phaser.Scene, state: EditorState) {
    this.scene = scene;
    this.state = state;

    // ── Background ──
    this.container = scene.add.container(0, TL_Y).setDepth(10);
    const bg = scene.add.graphics();
    fillTechRect(bg, 0, 0, CANVAS_W, TIMELINE_H, 0x0e0e1e, 0.95);
    drawTechRect(bg, 0, 0, CANVAS_W, TIMELINE_H, TECH.DIM, 0.4, 1);
    this.container.add(bg);

    // ── Label ──
    const label = scene.add.text(10, 6, 'TIMELINE', {
      fontSize: '10px', color: '#666688', fontFamily: 'monospace',
    });
    this.container.add(label);

    // ── Scrollable card area ──
    this.scrollContainer = scene.add.container(CARD_PAD, 20);
    this.container.add(this.scrollContainer);

    // ── Insertion marker (hidden by default) ──
    this.insertionMarker = scene.add.graphics();
    this.insertionMarker.setVisible(false);
    this.scrollContainer.add(this.insertionMarker);

    // ── Scroll mask ──
    const maskShape = scene.make.graphics({});
    maskShape.fillRect(0, TL_Y + 18, CANVAS_W, TIMELINE_H - 18);
    this.scrollContainer.setMask(new Phaser.Display.Masks.GeometryMask(scene, maskShape));

    // ── Mouse wheel / trackpad horizontal scroll ──
    scene.input.on('wheel', (_p: Phaser.Input.Pointer, _gx: any, dx: number, dy: number) => {
      const ptr = scene.input.activePointer;
      if (ptr.y < TL_Y) return;
      // Use deltaX (trackpad horizontal swipe) + deltaY (mouse wheel / trackpad vertical)
      const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
      const maxScroll = Math.max(0, (this.cards.length + 1) * (CARD_W + CARD_GAP) - CANVAS_W + CARD_PAD * 2);
      this.scrollX = Phaser.Math.Clamp(this.scrollX + delta * 0.8, 0, maxScroll);
      this.scrollContainer.x = CARD_PAD - this.scrollX;
    });

    // ── Global drag tracking ──
    scene.input.on('pointermove', (ptr: Phaser.Input.Pointer) => {
      if (this.dragIndex < 0) return;
      // Only track drag within the timeline zone
      if (ptr.y < TL_Y || ptr.y > CANVAS_H) return;
      const dx = Math.abs(ptr.x - this.dragStartX);
      if (!this.isDragging && dx > DRAG_THRESHOLD) {
        this.isDragging = true;
      }
      if (this.isDragging) {
        this.updateDragVisual(ptr.x);
      }
    });

    scene.input.on('pointerup', () => {
      if (this.isDragging) {
        this.finishDrag();
      }
      this.dragIndex = -1;
      this.isDragging = false;
      this.insertionMarker.setVisible(false);
    });

    // ── Progress bar: track step start time + 20fps refresh during preview ──
    EventBus.on(CinematicEvents.STEP_STARTED, this.onPlayStepStarted, this);
    EventBus.on(EditorEvents.PREVIEW_START, this.onPreviewBegin, this);
    EventBus.on(EditorEvents.PREVIEW_STOP, this.onPreviewEnd, this);

    this.rebuild();
  }

  private onPlayStepStarted = (): void => {
    this.playStartTime = Date.now();
  };

  private onPreviewBegin = (): void => {
    this.playStartTime = Date.now();
    // Start a 20fps timer to animate progress bars
    if (this.progressTimer) this.progressTimer.remove();
    this.progressTimer = this.scene.time.addEvent({
      delay: 50, loop: true,
      callback: () => this.updateProgressBars(),
    });
  };

  private onPreviewEnd = (): void => {
    if (this.progressTimer) { this.progressTimer.remove(); this.progressTimer = null; }
    this.clearProgressBars();
  };

  private updateProgressBars(): void {
    const playing = this.state.playingStepIndex;
    if (playing < 0) return;

    // Clear all bars first
    this.clearProgressBars();

    const step = this.state.sequence.steps[playing];
    if (!step || playing >= this.cards.length) return;

    const duration = getStepDuration(step);
    const card = this.cards[playing];

    let pg = this.progressBars[playing];
    if (!pg) {
      pg = this.scene.add.graphics();
      card.add(pg);
      this.progressBars[playing] = pg;
    }
    pg.clear();

    if (duration === null || duration === 0) {
      // Indeterminate — pulsing bar
      const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 200);
      pg.fillStyle(0x44ff88, 0.6);
      pg.fillRect(2, CARD_H - 5, (CARD_W - 4) * pulse, 3);
    } else {
      // Determinate — progress bar
      const elapsed = Date.now() - this.playStartTime;
      const progress = Phaser.Math.Clamp(elapsed / duration, 0, 1);
      pg.fillStyle(0x44ff88, 0.9);
      pg.fillRect(2, CARD_H - 5, (CARD_W - 4) * progress, 3);
    }
  }

  private clearProgressBars(): void {
    for (let i = 0; i < this.progressBars.length; i++) {
      if (this.progressBars[i]) {
        this.progressBars[i]!.clear();
      }
    }
  }

  /** Rebuild all cards from scratch */
  rebuild(): void {
    // Keep insertion marker — remove everything else
    this.scrollContainer.removeAll(true);
    this.cards = [];
    this.progressBars = [];
    this.insertionMarker = this.scene.add.graphics();
    this.insertionMarker.setVisible(false);
    this.scrollContainer.add(this.insertionMarker);

    const steps = this.state.sequence.steps;
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const meta = getStepMeta(step.type);
      const x = i * (CARD_W + CARD_GAP);
      const card = this.createCard(x, 0, step, meta.label, meta.color, i);
      this.scrollContainer.add(card);
      this.cards.push(card);
    }

    // ── +ADD card at the end ──
    const addX = steps.length * (CARD_W + CARD_GAP);
    const addCard = this.createAddCard(addX, 0);
    this.scrollContainer.add(addCard);

    // Clamp scroll to new content width (prevents stale offset after step removal)
    const maxScroll = Math.max(0, (steps.length + 1) * (CARD_W + CARD_GAP) - CANVAS_W + CARD_PAD * 2);
    this.scrollX = Phaser.Math.Clamp(this.scrollX, 0, maxScroll);
    this.scrollContainer.x = CARD_PAD - this.scrollX;
  }

  /** Create the +ADD card that appends a new step at the end */
  private createAddCard(x: number, y: number): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);

    // Dashed-style background
    const bg = this.scene.add.graphics();
    fillTechRect(bg, 0, 0, CARD_W, CARD_H, 0x111122, 0.5);
    drawTechRect(bg, 0, 0, CARD_W, CARD_H, TECH.DIM, 0.3, 1);
    card.add(bg);

    // + icon
    const plus = this.scene.add.text(CARD_W / 2, CARD_H / 2 - 8, '+', {
      fontSize: '24px', color: '#334466', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    card.add(plus);

    const label = this.scene.add.text(CARD_W / 2, CARD_H / 2 + 14, 'ADD', {
      fontSize: '10px', color: '#334466', fontFamily: 'monospace',
    }).setOrigin(0.5);
    card.add(label);

    // Hit area
    const hitArea = this.scene.add.rectangle(CARD_W / 2, CARD_H / 2, CARD_W, CARD_H, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    hitArea.on('pointerover', () => { plus.setColor('#44aaff'); label.setColor('#44aaff'); });
    hitArea.on('pointerout', () => { plus.setColor('#334466'); label.setColor('#334466'); });
    hitArea.on('pointerdown', () => {
      const items = STEP_TYPE_METAS.map(m => ({ label: `${m.icon} ${m.label}`, value: m.type, description: m.label }));
      showSelectionPopup(this.scene, 'Add Step', items, (val) => {
        const step = createDefaultStep(val as CinematicStep['type']);
        applySmartDialogueDefaults(step, this.state.sequence.steps);
        this.state.sequence.steps.push(step);
        this.state.selectedStepIndex = this.state.sequence.steps.length - 1;
        EventBus.emit(EditorEvents.STEP_ADDED);
      });
    });
    card.add(hitArea);

    return card;
  }

  /**
   * Refresh visual state (selection + playing highlight) without rebuilding.
   *
   * Card visual states:
   *   - **Playing** : green border (0x44ff88), lineW=3
   *   - **Selected**: yellow border (TECH.ACCENT), lineW=2.5
   *   - **Past**    : dim border (TECH.DIM), alpha=0.2
   *   - **Normal**  : dim border (TECH.DIM), alpha=0.4
   *
   * Every call resets container alpha and bg fill to defaults, preventing
   * stale visuals from the drag-to-reorder system (which dims the dragged
   * card via `container.setAlpha(0.4)` during drag).
   */
  refresh(): void {
    const playing = this.state.playingStepIndex;
    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i];

      // Always reset container alpha (drag system may leave it at 0.4)
      card.setAlpha(1);

      // Always reset bg fill to default (guard against any stale visual state)
      const bg = card.getData('bg') as Phaser.GameObjects.Rectangle | null;
      if (bg) bg.setFillStyle(0x181830, 0.9);

      const border = card.getData('border') as Phaser.GameObjects.Graphics;
      if (!border) continue;
      border.clear();

      const isPlaying = playing >= 0 && i === playing;
      const isSelected = i === this.state.selectedStepIndex;

      if (isPlaying) {
        drawTechRect(border, 0, 0, CARD_W, CARD_H, 0x44ff88, 1, 3);
      } else if (isSelected) {
        drawTechRect(border, 0, 0, CARD_W, CARD_H, TECH.ACCENT, 1, 2.5);
      } else {
        const isPast = playing >= 0 && i < playing;
        drawTechRect(border, 0, 0, CARD_W, CARD_H, TECH.DIM, isPast ? 0.2 : 0.4, 1);
      }
    }

    // Auto-scroll to keep playing card visible
    if (playing >= 0) {
      const cardLeft = playing * (CARD_W + CARD_GAP);
      const cardRight = cardLeft + CARD_W;
      const viewLeft = this.scrollX;
      const viewRight = this.scrollX + CANVAS_W - CARD_PAD * 2;
      if (cardRight > viewRight) {
        this.scrollX = cardRight - (CANVAS_W - CARD_PAD * 2) + CARD_GAP;
        this.scrollContainer.x = CARD_PAD - this.scrollX;
      } else if (cardLeft < viewLeft) {
        this.scrollX = Math.max(0, cardLeft - CARD_GAP);
        this.scrollContainer.x = CARD_PAD - this.scrollX;
      }
    }
  }

  // ── Drag-to-reorder ──
  // Flow: pointerdown on card → sets dragIndex + dragStartX
  //       pointermove (dx > DRAG_THRESHOLD) → isDragging=true, dims card to 0.4, shows insertion marker
  //       pointerup → finishDrag() reorders steps + restores alpha to 1
  // Safety: refresh() also resets alpha=1 on all cards, so even if finishDrag
  //         is bypassed (edge case), the next selection change cleans up.

  /** Get insertion index from pointer x (in screen space) */
  private getInsertIndex(screenX: number): number {
    const localX = screenX - this.scrollContainer.x - this.container.x;
    const slotW = CARD_W + CARD_GAP;
    const idx = Math.round(localX / slotW);
    return Phaser.Math.Clamp(idx, 0, this.cards.length);
  }

  private updateDragVisual(screenX: number): void {
    const insertIdx = this.getInsertIndex(screenX);

    // Draw yellow insertion line
    this.insertionMarker.clear();
    this.insertionMarker.setVisible(true);
    const markerX = insertIdx * (CARD_W + CARD_GAP) - CARD_GAP / 2;
    this.insertionMarker.lineStyle(3, TECH.ACCENT, 1);
    this.insertionMarker.lineBetween(markerX, -2, markerX, CARD_H + 2);
    // Small triangles at top and bottom
    this.insertionMarker.fillStyle(TECH.ACCENT, 1);
    this.insertionMarker.fillTriangle(markerX - 5, -2, markerX + 5, -2, markerX, 5);
    this.insertionMarker.fillTriangle(markerX - 5, CARD_H + 2, markerX + 5, CARD_H + 2, markerX, CARD_H - 5);

    // Dim the dragged card
    if (this.dragIndex >= 0 && this.dragIndex < this.cards.length) {
      this.cards[this.dragIndex].setAlpha(0.4);
    }
  }

  private finishDrag(): void {
    const ptr = this.scene.input.activePointer;
    let insertIdx = this.getInsertIndex(ptr.x);

    // Restore alpha
    if (this.dragIndex >= 0 && this.dragIndex < this.cards.length) {
      this.cards[this.dragIndex].setAlpha(1);
    }

    // Adjust insert index if moving forward (splice removes first)
    if (insertIdx > this.dragIndex) insertIdx--;
    if (insertIdx === this.dragIndex) return; // no change

    // Reorder the steps array
    const steps = this.state.sequence.steps;
    const [moved] = steps.splice(this.dragIndex, 1);
    steps.splice(insertIdx, 0, moved);

    // Update selection to follow the moved step
    if (this.state.selectedStepIndex === this.dragIndex) {
      this.state.selectedStepIndex = insertIdx;
    } else if (this.state.selectedStepIndex > this.dragIndex && this.state.selectedStepIndex <= insertIdx) {
      this.state.selectedStepIndex--;
    } else if (this.state.selectedStepIndex < this.dragIndex && this.state.selectedStepIndex >= insertIdx) {
      this.state.selectedStepIndex++;
    }

    EventBus.emit(EditorEvents.STEPS_REORDERED);
  }

  // ── Card creation ──
  // Each card container stores two data keys for refresh():
  //   'bg'     → Phaser.GameObjects.Rectangle (background fill, reset each refresh)
  //   'border' → Phaser.GameObjects.Graphics   (selection/playing border, redrawn each refresh)

  private createCard(x: number, y: number, step: any, typeLabel: string, color: number, index: number): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);
    const stepId = step.id as string;

    // Background
    const bg = this.scene.add.rectangle(CARD_W / 2, CARD_H / 2, CARD_W, CARD_H, 0x181830, 0.9);
    card.add(bg);
    card.setData('bg', bg);

    // Color bar at top
    const bar = this.scene.add.graphics();
    bar.fillStyle(color, 0.8);
    bar.fillRect(2, 2, CARD_W - 4, 6);
    card.add(bar);

    // Type label
    const lbl = this.scene.add.text(4, 14, typeLabel, {
      fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
    });
    card.add(lbl);

    // Step summary (short info per type)
    const summary = this.getStepSummary(step);
    const sumTxt = this.scene.add.text(4, 30, summary, {
      fontSize: '9px', color: '#8888aa', fontFamily: 'monospace',
    });
    card.add(sumTxt);

    // Duration
    const dur = getStepDuration(step);
    const durTxt = this.scene.add.text(4, 46, fmtMs(dur), {
      fontSize: '9px', color: '#557755', fontFamily: 'monospace',
    });
    card.add(durTxt);

    // Index number
    const numTxt = this.scene.add.text(4, CARD_H - 16, `${index + 1}`, {
      fontSize: '10px', color: '#444466', fontFamily: 'monospace',
    });
    card.add(numTxt);

    const isDisabled = !!step.disabled;

    // Delete button (top-right corner)
    // Duplicate button
    const dupBtn = this.scene.add.text(CARD_W - 28, 2, '⊕', {
      fontSize: '10px', color: '#335533', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    dupBtn.on('pointerover', () => dupBtn.setColor('#44ff44'));
    dupBtn.on('pointerout', () => dupBtn.setColor('#335533'));
    dupBtn.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      ptr.event.stopPropagation();
      const clone = JSON.parse(JSON.stringify(this.state.sequence.steps[index]));
      clone.id = `${clone.type}_${Date.now().toString(36)}`;
      this.state.sequence.steps.splice(index + 1, 0, clone);
      this.state.selectedStepIndex = index + 1;
      EventBus.emit(EditorEvents.STEP_ADDED);
    });
    card.add(dupBtn);

    // Delete button
    const delBtn = this.scene.add.text(CARD_W - 14, 2, '✕', {
      fontSize: '10px', color: '#553333', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    delBtn.on('pointerover', () => delBtn.setColor('#ff4444'));
    delBtn.on('pointerout', () => delBtn.setColor('#553333'));
    delBtn.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      ptr.event.stopPropagation();
      showConfirmDialog(this.scene, `Delete step "${stepId}"?`, () => {
        this.state.sequence.steps.splice(index, 1);
        if (this.state.selectedStepIndex >= this.state.sequence.steps.length) {
          this.state.selectedStepIndex = this.state.sequence.steps.length - 1;
        }
        EventBus.emit(EditorEvents.STEP_REMOVED);
      });
    });
    card.add(delBtn);

    // Border (selected state)
    const border = this.scene.add.graphics();
    const isSelected = index === this.state.selectedStepIndex;
    drawTechRect(border, 0, 0, CARD_W, CARD_H, isSelected ? TECH.ACCENT : TECH.DIM, isSelected ? 1 : 0.4, isSelected ? 2 : 1);
    card.add(border);
    card.setData('border', border);

    // Hit area — click to select, right-click to delete, drag to reorder
    const hitArea = this.scene.add.rectangle(CARD_W / 2, CARD_H / 2, CARD_W, CARD_H, 0x000000, 0)
      .setInteractive({ useHandCursor: true });

    hitArea.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      if (ptr.rightButtonDown()) {
        showConfirmDialog(this.scene, `Delete step "${stepId}"?`, () => {
          this.state.sequence.steps.splice(index, 1);
          if (this.state.selectedStepIndex >= this.state.sequence.steps.length) {
            this.state.selectedStepIndex = this.state.sequence.steps.length - 1;
          }
          EventBus.emit(EditorEvents.STEP_REMOVED);
        });
      } else {
        // Start potential drag + select
        this.dragIndex = index;
        this.dragStartX = ptr.x;
        this.isDragging = false;
        this.state.selectedStepIndex = index;
        EventBus.emit(EditorEvents.STEP_SELECTED);
      }
    });
    card.add(hitArea);

    // Disabled overlay (dim + strikethrough, above hitArea so it's visible)
    if (isDisabled) {
      const dimOverlay = this.scene.add.rectangle(CARD_W / 2, CARD_H / 2, CARD_W, CARD_H, 0x000000, 0.5);
      card.add(dimOverlay);
      const strike = this.scene.add.graphics();
      strike.lineStyle(2, 0xff4444, 0.6);
      strike.lineBetween(4, CARD_H / 2, CARD_W - 4, CARD_H / 2);
      card.add(strike);
    }

    // Enable/disable toggle (bottom-right, above everything)
    const toggleColor = isDisabled ? 0x553333 : 0x338833;
    const toggleCircle = this.scene.add.circle(CARD_W - 12, CARD_H - 10, 5, toggleColor)
      .setStrokeStyle(1, isDisabled ? 0xff4444 : 0x44ff44)
      .setInteractive({ useHandCursor: true });
    toggleCircle.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      ptr.event.stopPropagation();
      step.disabled = !step.disabled;
      // Immediate sync to registry (no debounce) so playback always sees latest state
      registerSequence(JSON.parse(JSON.stringify(this.state.sequence)));
      EventBus.emit(EditorEvents.STEP_UPDATED);
      this.rebuild();
    });
    card.add(toggleCircle);

    return card;
  }

  /** Short summary text for a step (shown on card).
   *  Dialogue cards only show the speaker name — full text is in step panel when selected. */
  private getStepSummary(step: any): string {
    switch (step.type) {
      case 'dialogue':  return step.speaker?.split('.').pop()?.substring(0, 10) ?? '';
      case 'cameraPan': return typeof step.target === 'string' ? step.target : `${step.target?.x},${step.target?.y}`;
      case 'fakeClick': return typeof step.target === 'string' ? step.target : `${step.target?.x},${step.target?.y}`;
      case 'action':    return step.action ?? '';
      case 'wait':      return step.waitEvent?.substring(0, 10) ?? 'timeout';
      case 'recap':     return step.titleKey?.substring(0, 10) ?? '';
      case 'spotlight': return typeof step.target === 'string' ? step.target : 'custom';
      default:          return '';
    }
  }

  setVisible(visible: boolean): void { this.container.setVisible(visible); }
  destroy(): void {
    EventBus.off(CinematicEvents.STEP_STARTED, this.onPlayStepStarted, this);
    EventBus.off(EditorEvents.PREVIEW_START, this.onPreviewBegin, this);
    EventBus.off(EditorEvents.PREVIEW_STOP, this.onPreviewEnd, this);
    if (this.progressTimer) { this.progressTimer.remove(); this.progressTimer = null; }
    this.container.destroy();
  }
}
